"use client";
import Image from "next/image";
import { useContext, useEffect, useRef, useState } from "react";
import { useContractRead, useReadContract } from "wagmi";

import ThemeInterface from "../abi/ThemeInterface";
import TrackRenderer from "../abi/TrackRenderer";
import { ThemeContext } from "../pages/_app";
import { getTheme, unpackTheme } from "../utils/getTheme";

export type AppTouchEvent = TouchEvent;

const StaticTrack = ({
  track,
  seed,
  interactionEnabled = false,
  showAds = false,
  children,
  ignoreTheme = false,
}: {
  children?: React.ReactNode;
  track?: string;
  seed: string;
  interactionEnabled?: boolean;
  showAds?: boolean;
  ignoreTheme?: boolean;
}) => {
  const [theme, setTheme] = useState(getTheme(seed));

  const { setThemeColor } = useContext(ThemeContext);

  const { data: themeAddress } = useReadContract({
    ...TrackRenderer,
    address: TrackRenderer.rendererAddress(Number(seed)),
    functionName: "customTheme",
    args: [BigInt(seed)],
  });

  const { data: customTheme } = useReadContract({
    ...ThemeInterface,
    address: themeAddress,
    functionName: "themeColors",
    query: {
      enabled: Boolean(themeAddress),
    },
  });

  useEffect(() => {
    if (customTheme) {
      const _theme = unpackTheme(customTheme, seed);
      setTheme(_theme);
    }
  }, [customTheme]);

  useEffect(() => {
    if (ignoreTheme) return;
    setThemeColor(theme.background);
  }, [theme]);

  const svg = useRef<SVGSVGElement>(null);
  const bg = useRef<SVGRectElement>(null);
  const road = useRef<SVGPathElement>(null);
  const midline = useRef<SVGPathElement>(null);
  const finishLine = useRef<SVGRectElement>(null);
  const crashBarrier = useRef<SVGPathElement>(null);
  const startArea = useRef<SVGPathElement>(null);
  const midwayGuard = useRef<SVGRectElement>(null);

  const { data: _track } = useContractRead({
    ...TrackRenderer,
    address: TrackRenderer.rendererAddress(Number(seed)),
    functionName: "d",
    query: {
      enabled: !track && Boolean(seed),
    },
    args: [BigInt(seed || 0)],
  });

  useEffect(() => {
    if (!track && !_track) return;

    const path = road.current;
    const dashed = midline.current;

    path?.setAttribute("d", track || _track);
    dashed?.setAttribute("d", track || _track);
    startArea?.current?.setAttribute("d", track || _track);
    crashBarrier?.current?.setAttribute("d", track || _track);

    // path &&
    //   addStartingLine(path as unknown as SVGPathElement, startArea.current);
    // setDebug({ pointCount, start, points, track });
  }, [track, _track]);

  // useEffect(() => {
  //   if (isMounted) {
  //     const path = road.current;
  //     path && addStartingLine(path as unknown as SVGPathElement);
  //   }
  // }, [isMounted]);

  const checkerSize = 12;

  const { data: ads } = useContractRead({
    ...TrackRenderer,
    address: TrackRenderer.rendererAddress(Number(seed)),
    functionName: "getAds",
    query: {
      enabled: Boolean(seed) && Boolean(showAds),
    },
    args: [BigInt(seed || 0)],
  });

  return (
    <>
      {children}
      <svg
        ref={svg}
        className="absolute top-0 left-0 w-full h-full select-none"
        style={{
          overflow: "inherit",
          background: "transparent",
          userSelect: "none",
          pointerEvents: interactionEnabled ? "all" : "none",
          // background: theme.background,
        }}
        // fill={theme.background}
        id="canvas"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        viewBox="0 0 1200 1950"
      >
        <defs>
          <pattern
            id="pattern-checkers"
            x="0"
            y="0"
            width={checkerSize * 2}
            height={checkerSize * 2}
            patternUnits="userSpaceOnUse"
            fill="white"
            transform="rotate(270)"
          >
            <rect
              className="checker"
              x="0"
              width={checkerSize}
              height={checkerSize}
              y="0"
            ></rect>
            <rect
              className="checker"
              x={checkerSize}
              width={checkerSize}
              height={checkerSize}
              y={checkerSize}
            ></rect>
          </pattern>

          <filter
            id={`st-noise-${seed}`}
            filterUnits="objectBoundingBox"
            colorInterpolationFilters="sRGB"
          >
            {["Mono", "Ringers", "Coinbase"].includes(theme.name) ? (
              <feComponentTransfer>
                <feFuncR type="identity" />
                <feFuncG type="identity" />
                <feFuncB type="identity" />
                <feFuncA type="identity" />
              </feComponentTransfer>
            ) : (
              <>
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.25"
                  numOctaves="2"
                  result="noise"
                />
                <feComposite in="SourceGraphic" in2="noise" operator="in" />
              </>
            )}
          </filter>
        </defs>
        {ignoreTheme && (
          <rect
            ref={bg}
            style={{ zIndex: 1 }}
            width="100%"
            height="100%"
            fill={theme.background}
          ></rect>
        )}
        {showAds &&
          ads?.map(({ src, rotation, ...pos }, i) => (
            <foreignObject
              key={i}
              {...pos}
              x={pos.x}
              y={pos.y}
              transform={`rotate(${rotation} ${pos.x + pos.width / 2} ${
                pos.y + pos.height / 2
              }) `}
            >
              <Image
                priority
                placeholder="blur"
                blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                loading="eager"
                alt="ad-image"
                quality={10}
                width={pos.width}
                height={pos.height}
                style={{
                  objectFit: "contain",
                  pointerEvents: "none",
                  touchAction: "none",
                  userSelect: "none",
                  borderRadius: "inherit",
                  zIndex: 2,
                }}
                src={src.replace(
                  "https://ipfs.io/",
                  "https://cloudflare-ipfs.com/"
                )}
              />
            </foreignObject>
          ))}
        <path
          ref={crashBarrier}
          strokeLinecap="round"
          fill="none"
          stroke={theme.crashBarrier}
          strokeWidth="100"
          strokeLinejoin="round"
          opacity={1}
        ></path>
        <path
          ref={road}
          strokeLinecap="round"
          fill="none"
          stroke={theme.road}
          strokeWidth="88"
          strokeLinejoin="round"
          opacity={1}
          filter={`url(#st-noise-${seed})`}
        ></path>
        <path
          ref={midline}
          strokeLinecap="round"
          fill="none"
          stroke={theme.dash}
          strokeDasharray={theme.strokeDashArray}
          strokeWidth="4"
          strokeLinejoin="round"
          opacity={1}
        ></path>
        {track && showAds ? (
          <>
            <path
              x="-100"
              y="-100"
              stroke="#FFFF00"
              strokeWidth="88"
              strokeLinejoin="round"
              opacity={0.75}
              fill="none"
              ref={startArea}
            ></path>
            <text
              x="-100"
              y="-100"
              textAnchor="middle"
              fontSize="46px"
              fill="black"
              className="font-Bebas"
              opacity={1}
            >
              <animate
                attributeName="font-size"
                attributeType="XML"
                values="42;54;42;"
                repeatCount="indefinite"
                begin="0s"
                dur="3s"
              />
              START &rarr;
            </text>

            <rect
              x="-100"
              y="-100"
              id="line-bg"
              width="18px"
              height="56px"
              fill="white"
              opacity={1}
            ></rect>
            <rect
              x="-100"
              y="-100"
              ref={finishLine}
              id="line"
              width="18px"
              height="56px"
              fill="url(#pattern-checkers)"
              stroke="black"
              opacity={1}
            ></rect>
            <rect
              x="-100"
              y="-100"
              ref={midwayGuard}
              id="mid-guard"
              fill="none"
              opacity={1}
            ></rect>
          </>
        ) : null}
      </svg>
    </>
  );
};

export default StaticTrack;
